import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings2, Save, Loader2 } from "lucide-react";
import { listPlans, updatePlan } from "../../lib/api/admin-plans";
import type { Plan, PlanUpdateRequest } from "../../types/usage";

export function PlansPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PlanUpdateRequest>({});

  const { data: plans, isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: listPlans,
  });

  const mutation = useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: PlanUpdateRequest }) =>
      updatePlan(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
      setEditingId(null);
    },
  });

  const startEditing = (plan: Plan) => {
    setEditingId(plan.id);
    setEditForm({
      display_name: plan.display_name,
      metering_mode: plan.metering_mode,
      chat_limit: plan.chat_limit,
      message_limit: plan.message_limit,
      is_active: plan.is_active,
    });
  };

  const saveEdit = (planId: string) => {
    mutation.mutate({ planId, data: editForm });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings2 className="w-6 h-6 text-brand-muted" />
        <h1 className="text-2xl font-bold text-brand-text">Plans</h1>
      </div>

      <div className="bg-brand-bg shadow rounded-card overflow-hidden">
        <table className="min-w-full divide-y divide-brand-border">
          <thead className="bg-brand-surface">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Interval</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Metering Mode</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Chat Limit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Message Limit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Active</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-brand-bg divide-y divide-brand-border">
            {plans?.map((plan) => (
              <tr key={plan.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand-text">
                  {editingId === plan.id ? (
                    <input
                      type="text"
                      value={editForm.display_name || ""}
                      onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                      className="border rounded px-2 py-1 text-sm w-full"
                    />
                  ) : (
                    plan.display_name
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">{plan.interval}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {editingId === plan.id ? (
                    <select
                      value={editForm.metering_mode || "messages"}
                      onChange={(e) => setEditForm({ ...editForm, metering_mode: e.target.value as "chats" | "messages" })}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="messages">Messages</option>
                      <option value="chats">Chats</option>
                    </select>
                  ) : (
                    plan.metering_mode
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {editingId === plan.id ? (
                    <input
                      type="number"
                      value={editForm.chat_limit || 0}
                      onChange={(e) => setEditForm({ ...editForm, chat_limit: parseInt(e.target.value) })}
                      className="border rounded px-2 py-1 text-sm w-20"
                    />
                  ) : (
                    plan.chat_limit
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {editingId === plan.id ? (
                    <input
                      type="number"
                      value={editForm.message_limit || 0}
                      onChange={(e) => setEditForm({ ...editForm, message_limit: parseInt(e.target.value) })}
                      className="border rounded px-2 py-1 text-sm w-20"
                    />
                  ) : (
                    plan.message_limit
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                  {editingId === plan.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.is_active ?? true}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    />
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs ${plan.is_active ? "bg-brand-teal/10 text-brand-teal" : "bg-brand-error/10 text-brand-error"}`}>
                      {plan.is_active ? "Active" : "Inactive"}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingId === plan.id ? (
                    <button
                      onClick={() => saveEdit(plan.id)}
                      disabled={mutation.isPending}
                      className="text-brand-primary hover:opacity-80 flex items-center gap-1"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  ) : (
                    <button
                      onClick={() => startEditing(plan)}
                      className="text-brand-primary hover:opacity-80"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
